import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Popup } from '../../../../components/Popup'
import { Button, BUTTON_TYPES } from '../../../../components/Button'
import { Typography } from '../../../../components/Typography'
import { setLoading } from '../../../../redux/store/common/slice'
import { useContractCategoryDetailsContext } from '../../Providers/ContractCategoryDetailsContextProvider'
import { useContractCategoryDetailsPopup } from '../../Providers/ContractCategoryDetailsPopupProvider'
import { ReactComponent as Edit16 } from 'assets/svg/Edit16.svg'
import { BTN_TXT, LABELS } from '../../../../constants'
import {
  Props,
  TEditExpectedSpendAndSavingsErrorType,
  TEditExpectedSpendAndSavingsFormType
} from './types'
import { Input } from '../../../../components/Input'
import { currencyFormatterTwoDecimals } from '../../../ContractDetails/ContractDetailsGeneral/ContractDetailsForm/utils'
import { validateMessages } from '../../../../helper/common'
import { Form } from 'antd'
import { CustomForm } from 'components/Forms/CustomForm'
import { AxiosResponse } from 'axios'
import { FieldWithLabel } from '../../../../components/FieldWithLabel/FieldWithLabel'
import { updateExpectedValuesRequest } from '../../api'

export const EditExpectedSavingsAndSpendPopup = ({
  selectedHS,
  updateHSList
}: Props) => {
  const { editExpectedSavingsAndSpendPopup } = useContractCategoryDetailsPopup()
  const { state, actions } = editExpectedSavingsAndSpendPopup
  const { open: openEditPopup, close: closeEditPopup } = actions
  const { state: stateContract } = useContractCategoryDetailsContext()
  const { details } = stateContract
  const [errors, setErrors] = useState<TEditExpectedSpendAndSavingsErrorType>(
    {}
  )
  const [form] = Form.useForm<TEditExpectedSpendAndSavingsFormType>()

  const dispatch = useDispatch()

  const onCloseEditPopup = useCallback(() => {
    closeEditPopup()
    form.resetFields()
  }, [form, closeEditPopup])

  const handleSave = useCallback(
    async (values: TEditExpectedSpendAndSavingsFormType) => {
      if (!details.uuid) {
        throw new Error('CC ID not provided')
      }
      const { uuid } = selectedHS[0]
      if (!uuid) {
        throw new Error('Hospital ID are not provided')
      }
      try {
        dispatch(setLoading(true))
        await updateExpectedValuesRequest(details.uuid, {
          hospitals: [
            {
              expected_spend: values.expected_spend ?? null,
              expected_savings: values.expected_savings ?? null,
              hospital: uuid
            }
          ]
        })
        updateHSList()
        onCloseEditPopup()
      } catch (error) {
        const typedResponse = error as AxiosResponse
        setErrors(typedResponse.data)
      } finally {
        dispatch(setLoading(false))
      }
    },
    [details, onCloseEditPopup, dispatch, selectedHS, updateHSList]
  )

  useEffect(() => {
    if (selectedHS.length === 1 && selectedHS[0].hospital_level) {
      const { expected_savings, expected_spend } = selectedHS[0]
      form.setFieldsValue({ expected_savings: expected_savings ?? undefined })
      form.setFieldsValue({ expected_spend: expected_spend ?? undefined })
    }
  }, [selectedHS, form])

  if (!selectedHS) return null

  return (
    <>
      {selectedHS.length === 1 && selectedHS[0].hospital_level && (
        <Button
          type={BUTTON_TYPES.PRIMARY}
          onClick={openEditPopup}
          icon={<Edit16 />}
          upperCase
        >
          {BTN_TXT.EDIT}
        </Button>
      )}
      <Popup
        {...state}
        onCancel={onCloseEditPopup}
        width={442}
        footer={
          <>
            <Button
              type={BUTTON_TYPES.DEFAULT}
              onClick={onCloseEditPopup}
              upperCase
            >
              {BTN_TXT.CANCEL}
            </Button>
            <Button
              type={BUTTON_TYPES.PRIMARY}
              form="edit-expected-spend-savings-form"
              htmlType="submit"
              upperCase
              danger
            >
              {BTN_TXT.SAVE}
            </Button>
          </>
        }
      >
        <CustomForm<TEditExpectedSpendAndSavingsFormType>
          form={form}
          validateMessages={validateMessages}
          onFinish={handleSave}
          id="edit-expected-spend-savings-form"
          className="column gap-24"
        >
          <FieldWithLabel title="Hospital">
            <Typography.Body2>{selectedHS[0]?.name}</Typography.Body2>
          </FieldWithLabel>
          <div className="row gap-16 space-between align-center mt-0">
            <Input.Number
              propsItem={{
                name: 'expected_spend',
                label: LABELS.EXPECTED_SPEND,
                help: errors.expected_spend?.[0],
                validateStatus: errors.expected_spend && 'error',
                rules: [
                  {
                    required: true
                  }
                ]
              }}
              propsInputNumber={{
                min: '0',
                max: '9999999999.99',
                maxLength: 15,
                controls: false,
                formatter: currencyFormatterTwoDecimals,
                placeholder: '0'
              }}
            />
            <Input.Number
              propsItem={{
                name: 'expected_savings',
                label: LABELS.EXPECTED_SAVINGS,
                help: errors.expected_savings?.[0],
                validateStatus: errors.expected_savings && 'error',
                rules: [
                  {
                    required: true
                  }
                ]
              }}
              propsInputNumber={{
                min: '0',
                maxLength: 15,
                max: '9999999999.99',
                controls: false,
                formatter: currencyFormatterTwoDecimals,
                placeholder: '0'
              }}
            />
          </div>
        </CustomForm>
      </Popup>
    </>
  )
}
